import React from "react";
import { useTranslation } from "react-i18next";

const featuresData = [
  {
    titleKey: "feature.ebmIntegration",
    descriptionKey: "feature.ebmDescription",
    icon: "receipt",
  },
  {
    titleKey: "feature.aiRecommendations",
    descriptionKey: "feature.aiDescription",
    icon: "spark",
  },
  {
    titleKey: "feature.storeManagement",
    descriptionKey: "feature.storeDescription",
    icon: "store",
  },
  {
    titleKey: "feature.analytics",
    descriptionKey: "feature.analyticsDescription",
    icon: "chart",
  },
  {
    titleKey: "feature.customizable",
    descriptionKey: "feature.customizableDescription",
    icon: "sliders",
  },
  {
    titleKey: "feature.seamlessIntegration",
    descriptionKey: "feature.seamlessIntegrationDescription",
    icon: "link",
  },
  {
    titleKey: "feature.fastSetup",
    descriptionKey: "feature.fastSetupDescription",
    icon: "bolt",
  },
  {
    titleKey: "feature.realTimeUpdates",
    descriptionKey: "feature.realTimeUpdatesDescription",
    icon: "sync",
  },
];

const FeatureIcon: React.FC<{ name: string }> = ({ name }) => {
  const paths: Record<string, React.ReactNode> = {
    receipt: (
      <>
        <path d="M7 3h10v18l-2-1.2L13 21l-2-1.2L9 21l-2-1.2V3Z" />
        <path d="M10 8h4M10 12h4M10 16h2" />
      </>
    ),
    spark: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />,
    store: (
      <>
        <path d="M4 10h16l-1.5-5h-13L4 10Z" />
        <path d="M6 10v10h12V10M9 20v-6h6v6" />
      </>
    ),
    chart: <path d="M5 19V5M5 19h14M9 16v-5M13 16V8M17 16v-8" />,
    sliders: <path d="M5 7h14M5 17h14M9 7v4M15 13v4" />,
    link: <path d="M10 13a5 5 0 0 0 7.1 0l1.4-1.4a5 5 0 0 0-7.1-7.1L10.7 5M14 11a5 5 0 0 0-7.1 0l-1.4 1.4a5 5 0 0 0 7.1 7.1l.7-.7" />,
    bolt: <path d="M13 2L5 13h6l-1 9 8-12h-6l1-8Z" />,
    sync: <path d="M17 7a7 7 0 0 0-12 4M7 7H5V5M7 17a7 7 0 0 0 12-4M17 17h2v2" />,
  };

  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
};

const Features: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section
      className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950"
      id="features"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary dark:text-pink-200">
            {t("feature.eyebrow")}
          </p>
          <h2 className="mt-3 text-3xl font-bold text-slate-950 sm:text-4xl dark:text-white">
            {t("feature.keyFeatures")}
          </h2>
          <p className="mt-4 text-lg leading-8 text-slate-600 dark:text-slate-300">
            {t("feature.featuresIntro")}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {featuresData.map((feature, index) => (
            <div
              key={index}
              className="group rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl hover:shadow-slate-900/10 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-white dark:bg-pink-300/10 dark:text-pink-100">
                <FeatureIcon name={feature.icon} />
              </div>
              <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                {t(feature.titleKey || "defaultTitle")}
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t(feature.descriptionKey || "defaultDescription")}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
